import React from "react";

/**
 * Range slider. Accessibility props (`id`, `name`, `aria-label`, `aria-labelledby`,
 * `aria-valuetext`, `aria-describedby`, `disabled`, and any other input attribute) are forwarded to
 * the native <input type="range"> so a <Label htmlFor> or an aria-label can name it.
 *
 * The inline <style> below is the default look; theme-sheet-product.css (section 26) restyles
 * `.optra-slider` through `--optra-fill` / `--optra-fill-dir` for the sheet theme.
 */
export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  className = "",
  style,
  fillDirection = "ltr",
  fillColor = "#60A5FA",
  trackColor = "rgba(96, 165, 250, 0.2)",
  ...inputProps
}) {
  const handleChange = (e) => {
    onValueChange && onValueChange([parseFloat(e.target.value)]);
  };

  const currentValue = value?.[0] || 0;
  const percentage = ((currentValue - min) / (max - min)) * 100;
  const gradientDirection = fillDirection === "rtl" ? "to left" : "to right";

  return (
    <div className="relative w-full" style={style}>
      <input
        {...inputProps}
        type="range"
        min={min}
        max={max}
        step={step}
        value={currentValue}
        onChange={handleChange}
        className={`optra-slider w-full h-2 rounded-full appearance-none cursor-pointer ${className}`}
        style={{
          "--optra-track-bg": `linear-gradient(${gradientDirection}, ${fillColor} 0%, ${fillColor} ${percentage}%, ${trackColor} ${percentage}%, ${trackColor} 100%)`,
          "--optra-fill": `${percentage}%`,
          "--optra-fill-dir": gradientDirection,
          background: "transparent",
          outline: 'none',
        }}
      />
      <style>{`
        .optra-slider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #60A5FA;
          border: 2px solid #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(96, 165, 250, 0.4);
          margin-top: -5px;
        }
        .optra-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #60A5FA;
          border: 2px solid #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(96, 165, 250, 0.4);
        }
        .optra-slider::-ms-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #60A5FA;
          border: 2px solid #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(96, 165, 250, 0.4);
        }
        .optra-slider::-webkit-slider-runnable-track {
          height: 8px;
          border-radius: 4px;
          background: var(--optra-track-bg);
        }
        .optra-slider::-moz-range-track {
          height: 8px;
          border-radius: 4px;
          background: var(--optra-track-bg);
        }
        .optra-slider::-ms-track {
          height: 8px;
          border-radius: 4px;
          background: var(--optra-track-bg);
        }
      `}</style>
    </div>
  );
}
